import type * as Y from "yjs";
import { applyChanges, type ReviewUser, type TextChange } from "../editor/collab";

export interface MatchOptions {
  regex: boolean;
  caseSensitive: boolean;
  wholeWord: boolean;
}

/** Marks project-wide replacements in Yjs transactions. */
export const REPLACE_ORIGIN = Symbol("replace");

/** Must stay in step with buildMatcher in server/src/search.ts, whose results we act on. Throws on a bad regex. */
export function buildMatcher(query: string, o: MatchOptions): RegExp {
  let source = o.regex ? query : query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (o.wholeWord) source = `(?<![A-Za-z0-9_])(?:${source})(?![A-Za-z0-9_])`;
  return new RegExp(source, `gm${o.caseSensitive ? "" : "i"}`);
}

/** Expand $1, $<name>, $& and $$ in a regex replacement, as String.prototype.replace does. */
export function expandReplacement(template: string, m: RegExpExecArray): string {
  return template.replace(/\$(\$|&|\d{1,2}|<([^>]*)>)/g, (all, what: string, name: string | undefined) => {
    if (what === "$") return "$";
    if (what === "&") return m[0];
    if (name !== undefined) return m.groups?.[name] ?? "";
    const n = Number(what);
    if (n >= 1 && n < m.length) return m[n] ?? "";
    // "$10" with fewer than ten groups is $1 followed by a 0.
    const one = Number(what[0]);
    if (what.length === 2 && one >= 1 && one < m.length) return (m[one] ?? "") + what[1];
    return all;
  });
}

function offsetOf(text: string, line: number, column: number): number {
  let pos = 0;
  for (let l = 1; l < line; l++) {
    const nl = text.indexOf("\n", pos);
    if (nl < 0) return -1;
    pos = nl + 1;
  }
  return pos + column;
}

/**
 * Replace matches in a doc as one change (recorded as suggestions with trackAs).
 * With `only`, just the match that starts there, if it's still there. Returns how many were replaced.
 */
export function replaceInText(
  ytext: Y.Text,
  re: RegExp,
  replacement: string,
  expand: boolean,
  trackAs: ReviewUser | null,
  only?: { line: number; column: number; length: number },
): number {
  const text = ytext.toString();
  const changes: TextChange[] = [];
  const add = (m: RegExpExecArray) =>
    changes.push({ from: m.index, to: m.index + m[0].length, insert: expand ? expandReplacement(replacement, m) : replacement });
  if (only) {
    const at = offsetOf(text, only.line, only.column);
    re.lastIndex = Math.max(0, at);
    const m = at < 0 ? null : re.exec(text);
    if (m && m.index === at && m[0].length === only.length) add(m);
  } else {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (m[0].length === 0) re.lastIndex++;
      else add(m);
    }
  }
  if (changes.length) applyChanges(ytext, changes, trackAs, REPLACE_ORIGIN, (from, to) => text.slice(from, to));
  return changes.length;
}
